import {
    Alert,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Stack,
    Switch,
    TextField,
} from '@mui/material';
import moment from 'moment';
import * as React from 'react';

import { DeveloperApiError, useDeveloperApi } from './api';
import { ALLOWED_IPS_HELP, parseAllowedIps, USER_AGENT_HELP } from './apiKeyColumns';
import type { ApiCredential, ApiCredentialUpdate, LegacyAccessFlag } from './apiKeyTypes';
import { describeError } from './components';
import { LEGACY_ACCESS_FLAGS } from './labels';

interface Props {
    /** The key being edited; null closes the dialog. */
    credential: ApiCredential | null;
    onClose: () => void;
    /** Called with the key as the backend answered it after the change. */
    onSaved: (credential: ApiCredential) => void;
}

interface Form {
    name: string;
    description: string;
    legacyAccess: string[];
    allowedIps: string;
    userAgentPattern: string;
    neverExpires: boolean;
    expires: string; // datetime-local, the browser's time zone
}

const LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';

const formOf = (c: ApiCredential): Form => ({
    name: c.name,
    description: c.description || '',
    legacyAccess: [...(c.legacy_access || [])],
    allowedIps: (c.allowed_ips || []).join('\n'),
    userAgentPattern: c.user_agent_pattern || '',
    neverExpires: !c.expires,
    expires: c.expires ? moment(c.expires).format(LOCAL_FORMAT) : '',
});

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

/** The PATCH body: only what the form changed (apiv2 ApiCredentialUpdate). */
export const changesOf = (c: ApiCredential, form: Form): ApiCredentialUpdate => {
    const body: ApiCredentialUpdate = {};
    if (form.name.trim() !== c.name) {
        body.name = form.name.trim();
    }
    if (form.description.trim() !== (c.description || '')) {
        body.description = form.description.trim();
    }
    if (c.scope === 'tour_operator') {
        const order = Object.keys(LEGACY_ACCESS_FLAGS);
        const flags = order.filter((f) => form.legacyAccess.includes(f));
        if (!sameList(flags, order.filter((f) => (c.legacy_access || []).includes(f)))) {
            body.legacy_access = flags as LegacyAccessFlag[];
        }
    }
    const ips = parseAllowedIps(form.allowedIps);
    if (!sameList(ips, c.allowed_ips || [])) {
        body.allowed_ips = ips;
    }
    if (form.userAgentPattern.trim() !== (c.user_agent_pattern || '')) {
        body.user_agent_pattern = form.userAgentPattern.trim() || null;
    }
    const expires = form.neverExpires || !form.expires ? null : moment(form.expires, LOCAL_FORMAT).toISOString();
    const before = c.expires ? moment(c.expires).format(LOCAL_FORMAT) : null;
    if ((expires === null) !== (before === null) || (expires !== null && form.expires !== before)) {
        body.expires = expires;
    }
    return body;
};

/** The backend's per-field reasons (`invalid_body` errors[{field, message, value?}]), by field. */
const fieldErrors = (error: unknown): Record<string, string> => {
    const out: Record<string, string> = {};
    if (error instanceof DeveloperApiError) {
        error.errors.forEach((e) => {
            if (e.field) {
                const why = e.value ? `"${e.value}": ${e.message}` : e.message;
                out[e.field] = out[e.field] ? `${out[e.field]}; ${why}` : why;
            }
        });
    }
    return out;
};

/** Developer -> API keys -> Edit: the client-editable fields of a key (PATCH /v2/edit/api_credential/{id}/,
 *  DSEC D1). Scope, status and access groups are not edited here; revoke and rotate are their own actions. */
const ApiKeyEditDialog = ({ credential, onClose, onSaved }: Props) => {
    const api = useDeveloperApi();
    const [form, setForm] = React.useState<Form | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);

    React.useEffect(() => {
        if (credential) {
            setForm(formOf(credential));
            setError(null);
        }
    }, [credential]);

    if (!credential || !form) {
        return null;
    }
    const set = (patch: Partial<Form>) => setForm({ ...form, ...patch });
    const body = changesOf(credential, form);
    const unchanged = Object.keys(body).length === 0;
    const errors = fieldErrors(error);
    const importKey = credential.scope === 'tour_operator';
    // A key something else manages keeps its expiry and restrictions (backend api_credential_in_use).
    const managed = !!credential.in_use;

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            onSaved(await api.updateApiCredential(credential.id, body));
        } catch (e) {
            setError(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={!!credential}
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="edit-api-key-title"
            // Above the app's side menu (zIndex 10000 in layout/main.js).
            sx={{ zIndex: 10002 }}
        >
            <DialogTitle id="edit-api-key-title">Edit API key</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <TextField
                        required
                        size="small"
                        label="Name"
                        value={form.name}
                        onChange={(ev) => set({ name: ev.target.value })}
                        inputProps={{ maxLength: 256 }}
                        error={!!errors.name || !form.name.trim()}
                        helperText={errors.name}
                        disabled={saving}
                    />
                    <TextField
                        size="small"
                        multiline
                        minRows={2}
                        label="Description"
                        value={form.description}
                        onChange={(ev) => set({ description: ev.target.value })}
                        inputProps={{ maxLength: 2000 }}
                        error={!!errors.description}
                        helperText={errors.description || 'What the key is for, and who uses it.'}
                        disabled={saving}
                    />
                    {importKey && (
                        <FormControl component="fieldset" error={!!errors.legacy_access} disabled={saving}>
                            <FormLabel component="legend">Legacy access flags</FormLabel>
                            <FormGroup row>
                                {Object.entries(LEGACY_ACCESS_FLAGS).map(([flag, label]) => (
                                    <FormControlLabel
                                        key={flag}
                                        label={label}
                                        control={(
                                            <Checkbox
                                                size="small"
                                                checked={form.legacyAccess.includes(flag)}
                                                onChange={(ev) => set({
                                                    legacyAccess: ev.target.checked
                                                        ? [...form.legacyAccess, flag]
                                                        : form.legacyAccess.filter((f) => f !== flag),
                                                })}
                                            />
                                        )}
                                    />
                                ))}
                            </FormGroup>
                            <FormHelperText>
                                {errors.legacy_access || 'What this key may read back through import/query and the MCP query tools. Without a flag, import/query answers 403 for that record type.'}
                            </FormHelperText>
                        </FormControl>
                    )}
                    {managed && <Alert severity="info" data-testid="api-key-managed">{credential.in_use_reason}</Alert>}
                    {!managed && (
                        <>
                            <TextField
                                size="small"
                                multiline
                                minRows={2}
                                label="Allowed IPs"
                                placeholder={'203.0.113.7\n198.51.100.0/24'}
                                value={form.allowedIps}
                                onChange={(ev) => set({ allowedIps: ev.target.value })}
                                InputProps={{ sx: { fontFamily: 'monospace' } }}
                                error={!!errors.allowed_ips}
                                helperText={errors.allowed_ips || ALLOWED_IPS_HELP}
                                disabled={saving}
                            />
                            <TextField
                                size="small"
                                label="User-agent pattern"
                                placeholder="MyPMS/*"
                                value={form.userAgentPattern}
                                onChange={(ev) => set({ userAgentPattern: ev.target.value })}
                                inputProps={{ maxLength: 256 }}
                                InputProps={{ sx: { fontFamily: 'monospace' } }}
                                error={!!errors.user_agent_pattern}
                                helperText={errors.user_agent_pattern || USER_AGENT_HELP}
                                disabled={saving}
                            />
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                                <FormControlLabel
                                    label="Never expires"
                                    control={<Switch checked={form.neverExpires} onChange={(ev) => set({ neverExpires: ev.target.checked })} disabled={saving} />}
                                />
                                <TextField
                                    size="small"
                                    type="datetime-local"
                                    label="Expires"
                                    value={form.expires}
                                    onChange={(ev) => set({ expires: ev.target.value, neverExpires: false })}
                                    InputLabelProps={{ shrink: true }}
                                    disabled={saving || form.neverExpires}
                                    error={!!errors.expires}
                                    helperText={errors.expires || 'When the key stops working (your time zone).'}
                                />
                            </Stack>
                        </>
                    )}
                    {error && !Object.keys(errors).length ? <Alert severity="error">{describeError(error)}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={save}
                    disabled={saving || unchanged || !form.name.trim()}
                    startIcon={saving ? <CircularProgress size={14} /> : undefined}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ApiKeyEditDialog;
